import React, { useMemo } from 'react';
import { t } from 'i18next';
import ReactFinalForm from 'components/form/ReactFinalForm';
import { IdentityField } from 'components/form/IdentityField';
import { TextFieldAdapter } from 'components/form/TextFieldAdapter';
import { DatePickerAdapter } from 'components/form/DatePickerAdapter';
import { AutocompleteAdapter } from 'components/form/AutocompleteAdapter';
import { required } from 'helpers/validators';
import { IOption } from 'interfaces';

export type MedicalServiceFormValues = {
  exam: string;
  laboratory: string;
  request_laboratory: string;
  request_department?: string;
  workstation?: string;
  date: string | Date;
  request_date: string | Date;
  quantity: number;
};

export interface CreateMedicalServiceProps {
  translationPrefix: string;
  onSubmitCreate: (values: MedicalServiceFormValues) => void;
  exams: IOption[];
  laboratories: IOption[];
  workstations: IOption[];
  setExam?: (opt: IOption | null) => void;
  setLab?: (opt: IOption | null) => void;
}

const classNames = (...parts: Array<string | false | undefined>) => parts.filter(Boolean).join(' ');

function translate(translationPrefix: string) {
  return (key: string) => (key.startsWith('global.') ? t(key) : t(`${translationPrefix}.${key}`));
}

function FieldRow({ className, children }: { className?: string; children: React.ReactNode }) {
  return <div className={classNames('my-2', className)}>{children}</div>;
}

type BaseField<Name extends keyof MedicalServiceFormValues> = {
  name: Name;
  label: string;
  className: string;
  required?: boolean;
  validate?: (value: unknown) => string | undefined;
};

type TextField<Name extends keyof MedicalServiceFormValues> = BaseField<Name> & {
  kind: 'text';
  type: 'text' | 'number';
  initialValue?: MedicalServiceFormValues[Name];
};

type DateField<Name extends keyof MedicalServiceFormValues> = BaseField<Name> & {
  kind: 'date';
  type: 'date';
};

type AutocompleteField<Name extends keyof MedicalServiceFormValues> = BaseField<Name> & {
  kind: 'autocomplete';
  type: 'text';
  options: IOption[];
  optionValue: 'code';
  optionLabel?: 'description';
  setValue?: (opt: IOption | null) => void;
};

type FieldConfig<Name extends keyof MedicalServiceFormValues = keyof MedicalServiceFormValues> =
  | TextField<Name>
  | DateField<Name>
  | AutocompleteField<Name>;

function RenderField({ field }: { field: FieldConfig }) {
  switch (field.kind) {
    case 'autocomplete':
      return (
        <IdentityField
          name={String(field.name)}
          component={AutocompleteAdapter}
          options={field.options}
          setValue={field.setValue}
          label={field.label}
          type={field.type}
          optionValue={field.optionValue}
          optionLabel={field.optionLabel}
          validate={field.validate}
          required={field.required}
        />
      );

    case 'date':
      return (
        <IdentityField
          name={String(field.name)}
          component={DatePickerAdapter}
          label={field.label}
          type={field.type}
          validate={field.validate}
          required={field.required}
        />
      );

    case 'text':
      return (
        <IdentityField
          name={String(field.name)}
          component={TextFieldAdapter}
          label={field.label}
          type={field.type}
          initialValue={field.initialValue}
          validate={field.validate}
          required={field.required}
        />
      );

    default: {
      const _never: never = field;
      return null;
    }
  }
}

/** ====== Component ====== */
export function CreateMedicalService({
  translationPrefix,
  onSubmitCreate,
  exams,
  laboratories,
  workstations,
  setExam,
  setLab
}: CreateMedicalServiceProps) {
  const t = useMemo(() => translate(translationPrefix), [translationPrefix]);

  /** Configuration-Drive UI */
  const fields = useMemo<FieldConfig[]>(
    () => [
      {
        kind: 'autocomplete',
        name: 'exam',
        className: 'w-full md:w-full',
        label: t('entity.exam'),
        type: 'text',
        options: exams,
        setValue: setExam,
        optionLabel: 'description',
        optionValue: 'code',
        validate: required,
        required: true
      },
      {
        kind: 'autocomplete',
        name: 'laboratory',
        className: 'w-full md:w-full',
        label: t('global.laboratory'),
        type: 'text',
        options: laboratories,
        setValue: setLab,
        optionValue: 'code',
        validate: required,
        required: true
      },
      {
        kind: 'text',
        name: 'request_laboratory',
        className: 'w-full md:w-full',
        label: t('global.request_laboratory'),
        type: 'text',
        required: true
      },
      {
        kind: 'text',
        name: 'request_department',
        className: 'w-full md:w-full',
        label: t('global.request_department'),
        type: 'text'
      },
      {
        kind: 'autocomplete',
        name: 'workstation',
        className: 'w-full md:w-full',
        label: t('global.workstation'),
        type: 'text',
        options: workstations,
        optionValue: 'code'
      },
      {
        kind: 'date',
        name: 'date',
        className: 'w-full md:w-1/2 md:pr-1',
        label: t('entity.service_date'),
        type: 'date',
        validate: required,
        required: true
      },
      {
        kind: 'date',
        name: 'request_date',
        className: 'w-full md:w-1/2 md:pr-1',
        label: t('entity.request_date'),
        type: 'date',
        validate: required,
        required: true
      },
      {
        kind: 'text',
        name: 'quantity',
        className: 'w-full md:w-full md:pl-1',
        label: t('entity.quantity'),
        type: 'number',
        initialValue: 1,
        required: true
      }
    ],
    [t, exams, laboratories, workstations, setExam, setLab]
  );

  return (
    <ReactFinalForm onSubmit={onSubmitCreate}>
      <div className="flex flex-wrap my-4">
        {fields.map((f) => (
          <FieldRow key={String(f.name)} className={f.className}>
            <RenderField field={f} />
          </FieldRow>
        ))}
      </div>
    </ReactFinalForm>
  );
}
